/**
 * CLI 28 — React Query hooks para consumir los endpoints de uso.
 *
 * AP-11: el tier que viene del backend es interno (`fast`/`balanced`/
 * `deep`). La traducción a etiquetas públicas vive en el componente UI
 * (no en este hook) para que tests del backend permanezcan estables.
 */
import { useQuery } from "@tanstack/react-query";

import { api } from "@/api/client";

export interface UsageTotals {
  input_tokens: number;
  output_tokens: number;
  cache_read_tokens: number;
  cache_write_tokens: number;
  calls: number;
  errors: number;
  estimated_cost_usd: number;
  /** null (no cero) cuando falta tipo de cambio: se muestra la moneda original. */
  estimated_cost_mxn: number | null;
}

export interface UsagePeriodBucket {
  period: string;
  input_tokens: number;
  output_tokens: number;
  cache_read_tokens: number;
  cache_write_tokens: number;
  calls: number;
  estimated_cost_usd: number;
  estimated_cost_mxn: number | null;
}

export interface UsageTierBucket {
  tier: string;
  calls: number;
  tokens: number;
  estimated_cost_usd: number;
}

export interface UsageFeatureBucket {
  feature: string;
  calls: number;
  tokens: number;
  estimated_cost_usd: number;
}

export interface UsageUserBucket {
  user_id: string;
  username: string | null;
  calls: number;
  tokens: number;
  estimated_cost_usd: number;
}

export interface UsageTenantBucket {
  tenant_id: string;
  calls: number;
  tokens: number;
  estimated_cost_usd: number;
}

export interface SelfUsageResponse {
  user_id: string;
  from: string;
  to: string;
  granularity: "day" | "hour";
  totals: UsageTotals;
  by_period: UsagePeriodBucket[];
  by_tier: UsageTierBucket[];
  by_feature: UsageFeatureBucket[];
}

export interface AdminTenantResponse {
  tenant_id: string | null;
  from: string;
  to: string;
  granularity: "day" | "hour";
  totals: UsageTotals;
  by_period: UsagePeriodBucket[];
  by_tenant: UsageTenantBucket[];
}

export interface AdminUserResponse {
  user_id: string | null;
  from: string;
  to: string;
  granularity: "day" | "hour";
  totals: UsageTotals;
  by_period: UsagePeriodBucket[];
  by_user: UsageUserBucket[];
  /** Con qué se gastó. Sólo dice algo útil cuando la consulta filtra por usuario. */
  by_feature: UsageFeatureBucket[];
  by_tier: UsageTierBucket[];
}

/** Una llamada al motor. AP-11: nunca trae `provider` ni `model`. */
export interface UsageCallRow {
  id: string;
  ts: string | null;
  user_id: string;
  username: string | null;
  feature: string;
  tier: string;
  input_tokens: number;
  output_tokens: number;
  cache_read_tokens: number;
  cache_write_tokens: number;
  tokens: number;
  latency_ms: number;
  status: string;
  error_kind: string | null;
  request_id: string;
  prompt_hash: string;
  estimated_cost_usd: number;
  estimated_cost_mxn: number | null;
}

export interface UsageCallsResponse {
  user_id: string | null;
  from: string;
  to: string;
  total: number;
  limit: number;
  offset: number;
  items: UsageCallRow[];
}

export interface UsageFilters {
  since?: string;
  until?: string;
  tier?: string;
  feature?: string;
  granularity?: "day" | "hour";
  tenant_id?: string;
  user_id?: string;
}

function buildQueryString(filters: UsageFilters): string {
  const params = new URLSearchParams();
  if (filters.since) params.set("since", filters.since);
  if (filters.until) params.set("until", filters.until);
  if (filters.tier) params.set("tier", filters.tier);
  if (filters.feature) params.set("feature", filters.feature);
  if (filters.granularity) params.set("granularity", filters.granularity);
  if (filters.tenant_id) params.set("tenant_id", filters.tenant_id);
  if (filters.user_id) params.set("user_id", filters.user_id);
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

export function useSelfUsage(filters: UsageFilters) {
  return useQuery({
    queryKey: ["usage", "self", filters],
    queryFn: () =>
      api<SelfUsageResponse>(`/api/v1/usage${buildQueryString(filters)}`),
  });
}

export function useUsageByTenant(filters: UsageFilters) {
  return useQuery({
    queryKey: ["usage", "by-tenant", filters],
    queryFn: () =>
      api<AdminTenantResponse>(`/api/v1/usage/by-tenant${buildQueryString(filters)}`),
  });
}

export function useUsageByUser(filters: UsageFilters) {
  return useQuery({
    queryKey: ["usage", "by-user", filters],
    queryFn: () =>
      api<AdminUserResponse>(`/api/v1/usage/by-user${buildQueryString(filters)}`),
  });
}

/**
 * Detalle llamada por llamada — el "de dónde salieron esos tokens".
 *
 * Un operador sólo recibe las suyas aunque mande otro `user_id`: el
 * backend lo ignora en vez de responder 403.
 */
export function useUsageCalls(filters: UsageFilters, page: { limit: number; offset: number }) {
  const qs = buildQueryString(filters);
  const sep = qs ? "&" : "?";
  return useQuery({
    queryKey: ["usage", "calls", filters, page],
    queryFn: () =>
      api<UsageCallsResponse>(
        `/api/v1/usage/calls${qs}${sep}limit=${page.limit}&offset=${page.offset}`,
      ),
  });
}
